/**
 * CONCIERGE-01: Concierge Enquiry Tab for CRM LeadDetail
 * Shows conversation transcript, collected data, handoff status,
 * consent evidence, and analytics for concierge-sourced leads.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { MessageCircle, Clock, Check, Phone, Mail, User, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function StatusBadge({ status }) {
  const config = {
    ACTIVE: { bg: "#dbeafe", color: "#1d4ed8", label: "Active" },
    COMPLETED: { bg: "#d1fae5", color: "#065f46", label: "Completed" },
    SUBMITTED: { bg: "#dcfce7", color: "#15803d", label: "Submitted" },
    ABANDONED: { bg: "#fee2e2", color: "#b91c1c", label: "Abandoned" },
  }[status] || { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span style={{
      padding: "2px 10px", borderRadius: "12px", fontSize: "12px",
      background: config.bg, color: config.color, fontWeight: "600"
    }}>
      {config.label}
    </span>
  );
}

function CollectedDataSummary({ data }) {
  if (!data || Object.keys(data).length === 0) return <div style={{ fontSize: "13px", color: "#9ca3af" }}>No data collected</div>;
  const labels = {
    event_type: "Event Type", location: "Location", event_date: "Event Date",
    date_flexibility: "Date Flexibility", guest_count_range: "Guest Count",
    services: "Services", budget_range_code: "Budget", contact_name: "Name",
    contact_mobile: "Mobile", contact_email: "Email", consent_given: "Consent"
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
      {Object.entries(data).filter(([k, v]) => v !== null && v !== undefined && k !== "consent_given").map(([key, val]) => (
        <div key={key} style={{ background: "#f9fafb", borderRadius: "8px", padding: "8px 12px" }}>
          <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px" }}>{labels[key] || key}</div>
          <div style={{ fontSize: "13px", color: "#1f2937", fontWeight: "500" }}>
            {Array.isArray(val) ? val.join(", ") : String(val)}
          </div>
        </div>
      ))}
    </div>
  );
}

function TranscriptStep({ step, index }) {
  return (
    <div style={{ padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
        <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#fdf6ec", border: "1.5px solid #e8d5a8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color: "#9a7133", flexShrink: 0 }}>
          {index + 1}
        </div>
        <div style={{ fontSize: "13px", color: "#6b7280" }}>{step.question_text}</div>
      </div>
      <div style={{ marginLeft: "32px" }}>
        <div style={{ display: "inline-block", background: "#C9A15F20", border: "1px solid #C9A15F40", borderRadius: "8px", padding: "4px 12px", fontSize: "13px", color: "#9a5c1a", fontWeight: "500" }}>
          {Array.isArray(step.answer) ? step.answer.join(", ") : step.answer === true ? "✓ Agreed" : String(step.answer || "—")}
        </div>
        {step.answered_at && (
          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "3px" }}>
            {new Date(step.answered_at).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ConciergeEnquiryTab({ lead }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    if (!lead?.concierge_session_id) { setLoading(false); return; }
    axios.get(`${BASE}/concierge/enquiries/${lead.concierge_session_id}`, { withCredentials: true })
      .then(r => setSession(r.data))
      .catch(e => setError(e.response?.data?.detail || "Failed to load session"))
      .finally(() => setLoading(false));
  }, [lead?.concierge_session_id]);

  if (!lead?.concierge_session_id) {
    return (
      <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af" }}>
        <MessageCircle size={32} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
        <div style={{ fontSize: "14px" }}>This lead did not originate from a concierge session.</div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>Loading session…</div>;
  if (error) return <div style={{ padding: "32px", color: "#b91c1c", fontSize: "14px" }}>Error: {error}</div>;
  if (!session) return null;

  const wa = session.whatsapp_handoff || {};

  return (
    <div style={{ padding: "20px", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Session summary */}
      <div style={{ background: "#fdf8f0", border: "1px solid #e8dfc9", borderRadius: "12px", padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
          <MessageCircle size={20} color="#C9A15F" />
          <div style={{ fontWeight: "600", fontSize: "15px", color: "#1f2937", flex: 1 }}>Concierge Session</div>
          <StatusBadge status={session.status} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", fontSize: "12px" }}>
          <div>
            <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Session ID</div>
            <div style={{ fontFamily: "monospace", color: "#374151", fontSize: "11px" }}>{session.session_id}</div>
          </div>
          <div>
            <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Started</div>
            <div style={{ color: "#374151" }}>{session.started_at ? new Date(session.started_at).toLocaleString() : "—"}</div>
          </div>
          <div>
            <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Source Page</div>
            <div style={{ color: "#374151" }}>{session.source_page || "—"}</div>
          </div>
          <div>
            <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Flow ID</div>
            <div style={{ fontFamily: "monospace", color: "#374151", fontSize: "11px" }}>{session.flow_id}</div>
          </div>
          <div>
            <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Steps Completed</div>
            <div style={{ color: "#374151" }}>{(session.step_history || []).length}</div>
          </div>
          {session.public_reference && (
            <div>
              <div style={{ color: "#9ca3af", marginBottom: "2px" }}>Public Reference</div>
              <div style={{ fontFamily: "monospace", color: "#374151", fontSize: "11px" }}>{session.public_reference}</div>
            </div>
          )}
        </div>
      </div>

      {/* Collected data */}
      <div>
        <div style={{ fontWeight: "600", fontSize: "13px", color: "#374151", marginBottom: "10px" }}>Collected Enquiry Data</div>
        <CollectedDataSummary data={session.collected_data} />
      </div>

      {/* WhatsApp handoff */}
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px", padding: "14px 16px" }}>
        <div style={{ fontWeight: "600", fontSize: "13px", color: "#15803d", marginBottom: "8px" }}>WhatsApp Handoff</div>
        <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
          <div>
            <span style={{ color: "#6b7280" }}>Mode: </span>
            <span style={{ color: "#374151", fontWeight: "500" }}>{wa.mode || "MODE_A"}</span>
          </div>
          <div>
            <span style={{ color: "#6b7280" }}>Status: </span>
            <span style={{ color: wa.status === "LINK_CLICKED" ? "#15803d" : "#374151", fontWeight: "500" }}>
              {wa.status === "LINK_CLICKED" ? "✓ Link Clicked" : wa.status || "Pending"}
            </span>
          </div>
          {wa.clicked_at && (
            <div>
              <span style={{ color: "#6b7280" }}>Clicked at: </span>
              <span style={{ color: "#374151" }}>{new Date(wa.clicked_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Consent evidence */}
      {session.consent && (
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px 16px" }}>
          <div style={{ fontWeight: "600", fontSize: "13px", color: "#374151", marginBottom: "8px" }}>Consent Evidence</div>
          <div style={{ fontSize: "12px", color: "#6b7280", lineHeight: "1.5", marginBottom: "8px" }}>
            {session.consent.text}
          </div>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Check size={12} color="#16a34a" />
              <span style={{ color: "#15803d", fontWeight: "500" }}>Given</span>
            </div>
            <div>
              <span style={{ color: "#6b7280" }}>Version: </span>
              <span style={{ color: "#374151" }}>{session.consent.version}</span>
            </div>
            {session.consent.timestamp && (
              <div>
                <span style={{ color: "#6b7280" }}>At: </span>
                <span style={{ color: "#374151" }}>{new Date(session.consent.timestamp).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transcript */}
      {(session.step_history || []).length > 0 && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>
          <button
            data-testid="concierge-transcript-toggle"
            onClick={() => setTranscriptOpen(o => !o)}
            style={{
              width: "100%", padding: "14px 16px", background: "#f9fafb",
              border: "none", cursor: "pointer", display: "flex", alignItems: "center",
              gap: "8px", fontSize: "13px", fontWeight: "600", color: "#374151",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {transcriptOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Conversation Transcript ({session.step_history.length} steps)
          </button>
          {transcriptOpen && (
            <div style={{ padding: "0 16px" }}>
              {(session.step_history || []).map((step, i) => (
                <TranscriptStep key={i} step={step} index={i} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
